import React, { useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import { changePassword } from "@service/auth";
import { removeCookie } from "@util/cookie";
import { cn } from "@util/tailwind";
import { useOuraMerchantButton } from "@oura/role";

interface PasswordManagementDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function PasswordManagementDialog({
    open,
    onOpenChange,
}: PasswordManagementDialogProps) {
    // Oura merchants get pill buttons; every other product keeps the shared shape.
    const pill = useOuraMerchantButton();
    const snackbar = useSnackbar();
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoadingChangePassword, setLoadingChangePassword] = useState(false);
    // const snackbar = useSettingsSnackbar();

    const handleChangePassword = async () => {
        // Validation
        if (!currentPassword.trim()) {
            snackbar.showError(
                "Validation Error",
                "Please enter your current password."
            );
            onOpenChange(false);
            return;
        }

        if (!newPassword.trim()) {
            snackbar.showError(
                "Validation Error",
                "Please enter a new password."
            );
            onOpenChange(false);
            return;
        }

        if (newPassword.length < 8) {
            snackbar.showError(
                "Weak Password",
                "Your new password must be at least 8 characters long."
            );
            onOpenChange(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            snackbar.showError(
                "Password Mismatch",
                "Your new password and confirmation password do not match."
            );
            onOpenChange(false);
            return;
        }

        setLoadingChangePassword(true);

        try {
            const resp = await changePassword({
                current_password: currentPassword,
                new_password: newPassword,
                confirm_new_password: confirmPassword,
            })

            if (resp.status === 200) {
                snackbar.showSuccess(
                    "Password Changed",
                    "Your password has been successfully changed."
                );
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");

                setTimeout(() => {
                    removeCookie("authToken");
                    window.location.href = "/";
                }, 3000)
            } else {
                snackbar.showError(
                    "Password Change Error",
                    resp.message || "Failed to change password."
                );
                console.error("Error changing password:", resp.message);
            }

            setLoadingChangePassword(false);

            // Reset form and close dialog
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            onOpenChange(false);
        } catch (error) {
            snackbar.showError(
                "Password Change Error",
                error instanceof Error ? error.message : "An unexpected error occurred."
            );
            console.error("Error changing password:", error);
        } finally {
            setLoadingChangePassword(false);
        }
    };

    const handleCancel = () => {
        // Reset form and close dialog
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                    <DialogDescription>
                        Enter your current password and choose a new password.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Current Password - Only show for non-Google SSO users */}
                    <div className="space-y-2">
                        <Label
                            htmlFor="current-password"
                            className="text-sm font-medium text-gray-900"
                        >
                            Current Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="current-password"
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="pr-10"
                            />

                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            >
                                {showCurrentPassword ? (
                                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <EyeIcon className="h-4 w-4 text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div className="space-y-2">
                        <Label
                            htmlFor="new-password"
                            className="text-sm font-medium text-gray-900"
                        >
                            New Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="new-password"
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="pr-10"
                            />

                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                                {showNewPassword ? (
                                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <EyeIcon className="h-4 w-4 text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Confirm New Password */}
                    <div className="space-y-2">
                        <Label
                            htmlFor="confirm-password"
                            className="text-sm font-medium text-gray-900"
                        >
                            Confirm New Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="pr-10"
                            />

                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <EyeIcon className="h-4 w-4 text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" className={pill} onClick={handleCancel} disabled={isLoadingChangePassword}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleChangePassword}
                        className={cn("bg-blue-600 hover:bg-blue-700 text-white", pill)}
                        disabled={isLoadingChangePassword}
                    >
                        {isLoadingChangePassword ? "Changing..." : "Change Password"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
